import React from "react";
import { Link } from "react-router-dom";
import { StarRating } from "./StarRating";
import { ShoppingCart, Check, ShieldCheck, Sparkles, Flame, Clock } from "lucide-react";
import { useCart, isPreOrder as _isPreOrder, isPreOrderSoldOut, isOnSale } from "../context/CartContext";
import { PriceDisplay, SaleBadge, BnplBadge } from "./PriceDisplay";
import { HeartButton } from "./HeartButton";
import { ProductImage } from "./ProductImage";

/* Small compact chip used in the header row above the product image —
 * Carbon Labs visual language: thin border, tiny mono uppercase text,
 * subtle glow. Colours are per-kind so verified / new / bestseller / sale
 * each have a distinct tone but all feel like technical status indicators
 * rather than marketing stickers. */
const TONES = {
    verified: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
    new:      "border-[rgba(0,229,255,0.35)] bg-[rgba(0,229,255,0.08)] text-[var(--cyan-hi)]",
    best:     "border-[rgba(196,198,200,0.4)] bg-[rgba(196,198,200,0.08)] text-[var(--silver-hi)]",
    sale:     "border-amber-400/40 bg-amber-400/10 text-amber-300",
};

const BadgeChip = ({ Icon, label, tone, hideOnMobile = false, testId }) => (
    <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[8.5px] font-mono font-semibold uppercase tracking-[0.14em] whitespace-nowrap border ${TONES[tone]} ${hideOnMobile ? "hidden sm:inline-flex" : ""}`}
        data-testid={testId}
    >
        {Icon && <Icon size={9} strokeWidth={2.4} />}
        {label}
    </span>
);

export const ProductCard = ({ product }) => {
    const { addItem } = useCart();
    const [added, setAdded] = React.useState(false);
    const isPreOrder = _isPreOrder(product);
    const preOrderSoldOut = isPreOrderSoldOut(product);

    const handleAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 1400);
    };

    // Pre-order lines bypass regular stock; sold-out is the allocation cap.
    const inStock = isPreOrder ? !preOrderSoldOut : product.stock > 0;
    const lowStock = !isPreOrder && product.stock > 0 && product.stock < 20;

    // Priority-capped differentiator stack. Pre-order is treated as
    // availability (bottom strip), NOT as a top-left chip — so it never
    // competes for space with Verified/New/Bestseller. Show max 2 chips on
    // mobile (< sm) and max 3 on sm+.
    const chips = [];
    if (isOnSale(product))                     chips.push({ kind: "sale", Icon: Flame, label: "Sale", testId: `sale-chip-${product.slug}` });
    if (product.has_coa)                       chips.push({ kind: "verified", Icon: ShieldCheck, label: "Verified", testId: `verified-badge-${product.slug}` });
    if (product.is_new)                        chips.push({ kind: "new", Icon: Sparkles, label: "New", testId: `new-chip-${product.slug}` });
    if (product.is_bestseller)                 chips.push({ kind: "best", label: "Bestseller", testId: `bestseller-chip-${product.slug}` });

    // Single availability descriptor — mutually exclusive with chips above.
    let stock;
    if (isPreOrder && preOrderSoldOut)  stock = { tone: "out",      label: "Pre-Order Sold Out",    testId: `stock-strip-${product.slug}` };
    else if (!inStock)                  stock = { tone: "out",      label: "Out of Stock",          testId: `stock-strip-${product.slug}` };
    else if (isPreOrder)                stock = { tone: "preorder", label: "Pre-Order",             testId: `stock-strip-${product.slug}` };
    else if (lowStock)                  stock = { tone: "low",      label: "Low Stock",             testId: `stock-strip-${product.slug}` };
    else                                stock = { tone: "stock",    label: "In Stock",              testId: `stock-strip-${product.slug}` };

    return (
        <Link
            to={`/product/${product.slug}`}
            className="product-card group flex flex-col"
            data-testid={`product-card-${product.slug}`}
        >
            {/* Header row — sits ABOVE the image, outside the photograph.
             * All differentiator chips live here so the vial is never
             * obscured. Heart sits on the right edge. */}
            <div className="flex items-center justify-between gap-2 px-3 pt-3 sm:px-4 sm:pt-4 min-h-[26px]">
                <div className="flex flex-wrap items-center gap-1 min-w-0">
                    {/* Preserve the legacy SaleBadge DOM node so tests
                     * looking up `sale-badge-<slug>` still find it. */}
                    <SaleBadge product={product} className="hidden" />
                    {chips.length === 0 ? (
                        <span
                            className="text-[8.5px] font-mono uppercase tracking-[0.28em] text-white/25"
                            data-testid={`serial-tag-${product.slug}`}
                        >
                            CL · {String(product.slug || "").slice(0, 8).toUpperCase()}
                        </span>
                    ) : (
                        chips.map((c, i) => (
                            <BadgeChip
                                key={c.kind}
                                Icon={c.Icon}
                                label={c.label}
                                tone={c.kind}
                                hideOnMobile={i >= 2}
                                testId={c.testId}
                            />
                        ))
                    )}
                </div>
                <div className="shrink-0" onClick={(e) => e.preventDefault()}>
                    <HeartButton product={product} size="sm" variant="ghost" />
                </div>
            </div>

            {/* Clean product image — no overlays at all. */}
            <ProductImage
                product={product}
                ratio="3 / 2"
                className="vial-shimmer mx-3 sm:mx-4 mt-2 rounded-sm"
                hoverScale
            />

            <div className="p-3 sm:p-5 flex flex-col gap-1.5 sm:gap-2 flex-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                        {product.dosage}
                    </span>
                    {product.reviews_count > 0 ? (
                        <StarRating value={product.rating} size={12} showValue count={product.reviews_count} />
                    ) : (
                        <span className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.2em] text-[var(--gold)]" data-testid={`research-grade-badge-${product.slug}`}>
                            Research Grade
                        </span>
                    )}
                </div>
                <h3 className="font-display text-sm sm:text-base font-semibold text-white leading-tight line-clamp-2">
                    {product.name}
                </h3>
                <p className="hidden sm:block text-sm text-[var(--text-secondary)] line-clamp-2 leading-relaxed">
                    {product.short_description}
                </p>

                {/* Availability — sits under the name, never over the vial */}
                <div className="flex items-center gap-1.5" data-testid={`stock-strip-${product.slug}`}>
                    <span
                        className={`w-1.5 h-1.5 rounded-full ${
                            stock.tone === "stock"    ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]" :
                            stock.tone === "preorder" ? "bg-[var(--cyan)] shadow-[0_0_6px_rgba(0,229,255,0.9)]" :
                            stock.tone === "low"      ? "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]" :
                                                        "bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.9)]"
                        }`}
                        aria-hidden="true"
                    />
                    <span
                        className={`text-[9px] sm:text-[10px] font-mono font-semibold uppercase tracking-[0.2em] ${
                            stock.tone === "stock"    ? "text-emerald-300" :
                            stock.tone === "preorder" ? "text-[var(--cyan-hi)]" :
                            stock.tone === "low"      ? "text-amber-300" :
                                                        "text-red-300"
                        }`}
                    >
                        {stock.label}
                    </span>
                </div>

                <div className="mt-auto pt-2 sm:pt-3 flex flex-col gap-1.5 sm:gap-2">
                    <PriceDisplay product={product} size="md" />
                    <BnplBadge product={product} />
                    <button
                        onClick={handleAdd}
                        disabled={!inStock}
                        data-testid={isPreOrder ? `pre-order-${product.slug}` : `add-to-cart-${product.slug}`}
                        className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] sm:text-xs font-semibold uppercase tracking-wider transition-all ${
                            added
                                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                                : isPreOrder
                                    ? "bg-[var(--cyan)] text-black hover:bg-[var(--cyan-hi)]"
                                    : "border border-[rgba(0,229,255,0.45)] text-[var(--cyan-hi)] hover:bg-[rgba(0,229,255,0.10)]"
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                        {added ? <Check size={14} /> : isPreOrder ? <Clock size={14} /> : <ShoppingCart size={14} />}
                        {added ? "Added" : (isPreOrder ? "Pre-order" : "Add to Cart")}
                    </button>
                </div>
            </div>
        </Link>
    );
};
